import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import UaePassImportButton from '~/components/UaePassImportButton';
import { Alert } from '~/components';
import { useAuthorizedRepresentativeContext } from '~/controllers/wizard';
import { isLoggedInWithUaePass, isUaePassFromUAE } from '~/utils/getDecodedJwt';
import { CheckmarkIcon } from '~/img';
import colors from '~/constants/colors';
import { UAEPASS_DISABLE } from '~/env';

const UaePassImportDocumentAlert = () => {
  const { t } = useTranslation('fillApplication');
  const location = useLocation();
  const isUaePass = isLoggedInWithUaePass();
  const isFromUAE = isUaePassFromUAE();

  const [authorizedRepresentativeContext] = useAuthorizedRepresentativeContext();
  const { onUaePassImport, activeTab, isSoleProprietor, ar1ImportStatus, ar2ImportStatus } =
    authorizedRepresentativeContext || {};
  const isAuthorizedRepresentative = location.pathname.includes('authorizedRepresentative');

  const uaePassStatus = activeTab === 'first' ? ar1ImportStatus : ar2ImportStatus;

  if (
    !isAuthorizedRepresentative ||
    !uaePassStatus ||
    !isUaePass ||
    !authorizedRepresentativeContext ||
    UAEPASS_DISABLE
  )
    return null;

  if (uaePassStatus === 'COMPLETE')
    return (
      <Alert
        message={t('Documents received from UAE PASS')}
        showIcon
        type="info"
        icon={<CheckmarkIcon style={{ color: colors.successColor }} />}
      />
    );

  if (uaePassStatus === 'UNAUTHORIZED')
    return (
      <StyledWarningAlert
        message={t('Please upgrade your account following instructions in the UAE PASS application')}
        type="warning"
      />
    );

  return (
    <>
      {uaePassStatus === 'REJECTED' && (
        <StyledWarningAlert
          message={t('You have rejected the data sharing request on UAE PASS. Please retry')}
          type="warning"
        />
      )}
      <StyledAlert
        message=""
        description={
          <>
            <Typography.Title level={5}>{t('Share documents using UAE PASS')}</Typography.Title>
            <Typography.Paragraph>
              {t(
                'Effortlessly and securely share authentic, up-to-date documents via UAE PASS. Your consent is required to share the following documents:',
              )}
            </Typography.Paragraph>
            <Typography.Paragraph>
              {isFromUAE ? (
                <em>{t('Passport, Emirates ID, POA (optional)')}</em>
              ) : (
                <em>{t('Visa, Emirates ID, POA (optional)')}</em>
              )}
            </Typography.Paragraph>
            {isSoleProprietor && (
              <Typography.Paragraph>
                {t(
                  'Additionally if you need to add POA as a signatory proof, you can add it to your UAE PASS app by requesting from Ministry of Justice department',
                )}
              </Typography.Paragraph>
            )}
            <UaePassImportButton
              id="uaepass_import"
              onClick={
                onUaePassImport &&
                (() => {
                  onUaePassImport(authorizedRepresentativeContext);
                })
              }
            />
          </>
        }
      />
    </>
  );
};

const StyledAlert = styled(Alert)`
  margin-bottom: 2px;

  div.ant-typography {
    font-size: 14px;
    line-height: 16px;
  }
`;

const StyledWarningAlert = styled(Alert)`
  font-size: 14px;
  margin-top: 2px;
  margin-bottom: 2px;
`;

export default UaePassImportDocumentAlert;
